import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    Pipe,
    PipeTransform, ViewChild
} from '@angular/core';
import {DigitalBusinessCard} from '../../../digital-business-card/digital-business-card.model';
import {IMAGE_TYPE} from '../dbc-mobile-form/dbc-mobile-form.component';
import {DBC_FORM_FIELD} from '../../dbc-onboard-v2.utils';
import {fadeIn} from '../../dbc-onboarding-v2-animations';
import {ColorPickerV2Component} from '../../../global-components/color-picker-v2/color-picker-v2.component';

@Pipe({
    name: 'inputState'
})
export class InputStatePipe implements PipeTransform {
    transform(field: string, value: string, focusedField: string | null, invalid: boolean = false): string {
        if (invalid) {
            return 'error';
        }
        if (focusedField === field) {
            return 'focused';
        }
        if (value.trim()) {
            return 'filled';
        }
        return 'empty';
    }
}

@Component({
    selector: 'app-form-layout-one',
    templateUrl: './form-layout-1.component.html',
    styleUrls: ['./form-layout-1.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [fadeIn]
})
export class FormLayout1Component implements OnInit, OnChanges {

    @Input() dbc: DigitalBusinessCard;
    @Input() userImageUrl: string;
    @Input() logoUrl: string;
    @Input() isFirstNameMissing: boolean;
    @Input() colorOptions: string[];
    @Input() fullPhoneNumber: string;
    @Input() countryNameCode: string;
    @Input() phoneNumber: string;
    @Input() isInvalidEmail: boolean;
    @Input() defaultColorPickerBackground: string;
    @Input() layoutPrimaryColor: string;
    @Input() emailInputErrorMessage: string;

    @Output() formUpdated = new EventEmitter<{ value: string, type: DBC_FORM_FIELD }>();
    @Output() imageSelected = new EventEmitter<{ type: IMAGE_TYPE, file: File }>();
    @Output() onCountryFlagChange = new EventEmitter<string>();
    @Output() openEditImageModal = new EventEmitter<IMAGE_TYPE>();
    @Output() formCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('colorPickerInput') colorPickerInput: ColorPickerV2Component;

    // enums
    IMAGE_TYPE = IMAGE_TYPE;
    DBC_FORM_FIELD = DBC_FORM_FIELD;

    // component variables
    countryCallingCode: string = '';
    focusedField: string | null = null;
    showAllFormFields: boolean = false;
    intlPhoneInputVisible: boolean = false;

    constructor(private readonly cd: ChangeDetectorRef) {}
    ngOnInit() {
        this.dbc.customizations.user_info_color = '#000000';
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dbc && changes.dbc.currentValue) {
            // update phone number field
            // @ts-ignore
            const phone = changes.dbc.currentValue?.phone_v2[0] as PhoneEntry;
            this.fullPhoneNumber = phone.value;
            this.phoneNumber = phone.value.split(' ')[1];
            if (!this.phoneNumber) {
                this.phoneNumber = this.fullPhoneNumber;
            }
            this.countryCallingCode = phone.value.split(' ')[0];
            if (!this.countryCallingCode) {
                this.countryCallingCode = '1';
                this.countryNameCode = 'US';
            }
        }
    }

    onFocus(field: string) {
        this.focusedField = field;

        if (field === DBC_FORM_FIELD.PHONE_NUMBER) {
            this.intlPhoneInputVisible = true;
            this.cd.detectChanges();
            this.focusIntlPhoneInput();
        }
    }

    onBlur(value: string, field: DBC_FORM_FIELD) {
        if (value) {
            this.onInputChange(value.trim(), field);
        }
        this.focusedField = null;
    }

    onPhoneFocusOut() {
        setTimeout(() => {

            // Check if focus is still within the intl-tel-input component
            const relatedTarget = document.activeElement;
            const telInputWrapper = document.querySelector('.iti');

            if (telInputWrapper && relatedTarget && telInputWrapper.contains(relatedTarget)) {
                // Focus is still within the intl-tel-input component or dropdown
                return;
            }
            this.intlPhoneInputVisible = false;
            if (this.focusedField !== DBC_FORM_FIELD.WEBSITE) {
                this.focusedField = null;
            }
        }, 100);
    }

    focusIntlPhoneInput() {
        const phoneInput = document.getElementById('phone');
        if (phoneInput) {
            phoneInput.focus();
        }
    }

    selectImageFromSystem(type: IMAGE_TYPE, event: Event) {
        const inputElement = event.target as HTMLInputElement;
        const file = inputElement.files?.[0];

        if (file) {
            this.imageSelected.emit({ type, file });
            inputElement.value = '';
        }
    }

    openEditModal(event: any, type: IMAGE_TYPE) {
        event.preventDefault()
        this.openEditImageModal.emit(type);
    }

    onInputChange(value: string, type: DBC_FORM_FIELD) {
        this.formUpdated.emit({value, type});
    }

    autoGrow(event: any): void {
        const textArea = event.target as HTMLTextAreaElement;
        textArea.style.height = '1px';
        const newHeight = textArea.scrollHeight;
        textArea.style.height = Math.max(newHeight, 22) + 'px';
    }

    onCountryChange(countryCallingCode) {
        this.onCountryFlagChange.emit(countryCallingCode);
    }

    onColorChange(color: string) {
        this.layoutPrimaryColor = color
        this.formUpdated.emit({value: color, type: DBC_FORM_FIELD.BACKGROUND_COLOR});
    }

    onAddMoreClick() {
        this.showAllFormFields = true;
    }

    goToNext(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (this.showAllFormFields) {
                switch (this.focusedField) {
                    case DBC_FORM_FIELD.FIRST_NAME:
                        this.focusNext(DBC_FORM_FIELD.DESIGNATION);
                        break;
                    case DBC_FORM_FIELD.DESIGNATION:
                        this.focusNext(DBC_FORM_FIELD.COMPANY);
                        break;
                    case DBC_FORM_FIELD.COMPANY:
                        this.focusNext(DBC_FORM_FIELD.EMAIL);
                        break;
                    case DBC_FORM_FIELD.EMAIL:
                        this.focusNext(DBC_FORM_FIELD.PHONE_NUMBER);
                        break;
                    case DBC_FORM_FIELD.PHONE_NUMBER:
                        this.focusNext(DBC_FORM_FIELD.WEBSITE);
                        break;
                    case DBC_FORM_FIELD.WEBSITE:
                        this.formCompleted.emit(); // Emit only on last field
                        break;
                }
            } else {
                switch (this.focusedField) {
                    case DBC_FORM_FIELD.FIRST_NAME:
                        this.focusNext(DBC_FORM_FIELD.EMAIL);
                        break;
                    case DBC_FORM_FIELD.EMAIL:
                        this.focusNext(DBC_FORM_FIELD.PHONE_NUMBER);
                        break;
                    case DBC_FORM_FIELD.PHONE_NUMBER:
                        this.formCompleted.emit(); // Emit only on last field
                        break;
                }
            }
        }
    }

    focusNext(field: string) {
        const nextElement: any = document.querySelector(`.${field}`);
        if (nextElement) {
            nextElement.focus();
            this.onFocus(field);
        }
    }

}
